if (!customElements.get('product-discount-offer')) {
  customElements.define(
    'product-discount-offer',
    class ProductDiscountOffer extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        this.productInfo = this.closest('product-info');
        if (window.ShopifyProductPricing && this.productInfo) this.pricing = new window.ShopifyProductPricing(this);
        this.productInfo?.addEventListener('click', this.handleAddonChange);
        this.productInfo?.addEventListener('change', this.handleAddonChange);
        this.refreshPrices();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
        this.unsubscribeVariantChange?.();
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.productInfo?.removeEventListener('click', this.handleAddonChange);
        this.productInfo?.removeEventListener('change', this.handleAddonChange);
        this.abortController?.abort();
        window.clearTimeout(this.offerTimeout);
        window.clearTimeout(this.copyButtonTimeout);
        this.pricing?.disconnect();
      }

      handleVariantChange = ({ data }) => {
        if (String(data?.sectionId) !== this.dataset.sectionId) return;
        const variantId = data?.variant?.id;
        if (!variantId) return;

        this.abortController?.abort();
        const source = data.html?.getElementById(`DiscountOffer-${this.dataset.sectionId}`)
          ?.querySelector('product-discount-offer');
        if (source) {
          this.replaceOffer(source);
        } else {
          this.setVariant(data.variant);
          if (!this.querySelector(`template[data-discount-offer-variant="${variantId}"]`)) this.fetchOffer();
        }
      };

      setVariant(variant) {
        const variantId = String(variant?.id || '');
        if (variantId === this.dataset.currentVariantId) return;

        const template = this.querySelector(`template[data-discount-offer-variant="${variantId}"]`);
        const current = this.querySelector('[data-discount-offer-current]');
        if (!current) return;

        current.innerHTML = template?.innerHTML || '';
        this.dataset.currentVariantId = variantId;
        this.dataset.available = String(Boolean(variant?.available));
        this.dataset.originalPrice = String(variant?.price || 0);
        this.dataset.compareAtPrice = String(variant?.compare_at_price || 0);
        this.dataset.fallbackPrice = template?.dataset.fallbackPrice || this.dataset.originalPrice;
        this.refreshPrices();
      }

      getPrice(variantId = this.dataset.currentVariantId) {
        if (this.pricing) return this.pricing.getPrice(variantId);
        return this.getDefaultPrice(variantId);
      }

      getDefaultPrice(variantId = this.dataset.currentVariantId) {
        const source = String(variantId) === this.dataset.currentVariantId ? this
          : this.querySelector(`template[data-discount-offer-variant="${variantId}"]`);
        if (!source) return null;
        const original = Number(source.dataset.originalPrice);
        const card = source === this ? this.querySelector('[data-discount-offer-current] [data-discount-offer-card]')
          : source.content.querySelector('[data-discount-offer-card]');
        const discounted = Number(card?.dataset.discountedPrice);
        return this.isOfferActive(card) && Number.isSafeInteger(discounted) && discounted >= 0 && discounted <= original
          ? discounted : original;
      }

      isOfferActive(card) {
        if (!card) return false;
        const now = Date.now();
        const startsAt = Number(card.dataset.startsAt) * 1000;
        const endsAt = Number(card.dataset.endsAt) * 1000;
        return (!startsAt || startsAt <= now) && (!endsAt || now < endsAt);
      }

      refreshPrices(schedulePreview = true) {
        window.clearTimeout(this.offerTimeout);
        if (schedulePreview) this.pricing?.schedule();
        const root = this.closest('product-info');
        const addonPrices = this.refreshAddonPrices(root);
        const card = this.querySelector('[data-discount-offer-current] [data-discount-offer-card]');
        const active = this.isOfferActive(card);
        if (card) card.hidden = !active;
        const price = this.getPrice();
        const originalPrice = Number(this.dataset.originalPrice);
        const priceRoot = root?.querySelector(`#price-${root.dataset.section}`);
        if (priceRoot) {
          priceRoot.setAttribute('aria-busy', String(this.pricing?.status === 'pending'));
          priceRoot.classList.toggle('hidden', !this.dataset.currentVariantId);
          const compareAtPrice = price < originalPrice
            ? originalPrice : Number(this.dataset.compareAtPrice);
          const onSale = compareAtPrice > price;
          const container = priceRoot.querySelector('.price');
          container?.classList.toggle('price--sold-out', this.dataset.available !== 'true');
          container?.classList.toggle('price--on-sale', onSale);
          container?.classList.toggle('price--no-compare', false);
          priceRoot.querySelectorAll('[data-product-unit-price]').forEach((node) => this.writePrice(node, price, true, this.pricing?.mainGroup?.max));
          priceRoot.querySelectorAll('[data-product-compare-price]').forEach((node) => {
            node.hidden = !onSale;
            this.writePrice(node, compareAtPrice || 0, false);
          });
          const note = priceRoot.querySelector('[data-product-price-note]');
          if (note) {
            note.hidden = true;
            note.textContent = '';
          }
        }

        this.dispatchEvent(new CustomEvent('product-price:change', {
          bubbles: true,
          detail: {
            sectionId: this.dataset.sectionId,
            productId: this.dataset.productId,
            variantId: this.dataset.currentVariantId,
            available: this.dataset.available === 'true',
            price,
            maxPrice: this.pricing?.mainGroup?.max ?? price,
          },
        }));

        const timedPrices = [card, ...addonPrices].filter(Boolean);
        if (timedPrices.length) {
          const nextBoundary = timedPrices.flatMap((node) => [node.dataset.startsAt, node.dataset.endsAt])
            .map((value) => Number(value) * 1000).filter((value) => value > Date.now()).sort((a, b) => a - b)[0];
          if (nextBoundary) {
            this.offerTimeout = window.setTimeout(() => {
              this.pricing?.invalidate();
              this.pricing?.schedule(true);
              this.refreshPrices();
              this.fetchOffer();
            }, Math.min(nextBoundary - Date.now() + 20, 2147483647));
          }
        }
      }

      refreshAddonPrices(root) {
        const sources = Array.from(root?.querySelectorAll('[data-addon-pricing]') || []);
        sources.forEach((node) => {
          node.dataset.price = node.dataset.originalPrice;
          if (node.matches('[data-accessory-variant]')) node.dataset.variantPrice = node.dataset.price;
        });
        if (this.pricing) {
          this.pricing.renderAddons();
          return sources;
        }
        root?.querySelectorAll('[data-cart-addon-input][data-addon-pricing]').forEach((input) => {
          const card = input.closest('[data-accessory-card]');
          const source = card?.querySelector(`[data-accessory-variant][data-variant-id="${input.value}"]`) || input;
          input.dataset.price = source.dataset.price;
          const price = card?.querySelector('[data-accessory-price]');
          if (price) {
            this.writePrice(price, Number(input.dataset.price));
            price.prepend('+');
          }
          const compare = card?.querySelector('[data-addon-compare-price]');
          if (compare) {
            const original = Number(source.dataset.originalPrice);
            const comparePrice = Number(input.dataset.price) < original ? original : Number(source.dataset.compareAtPrice || 0);
            compare.hidden = comparePrice <= Number(input.dataset.price);
            this.writePrice(compare.querySelector('[data-addon-compare-amount]'), comparePrice, false);
          }
        });
        return sources;
      }

      handleAddonChange = (event) => {
        const selector = event.type === 'change' ? '[data-cart-addon-input]'
          : '[data-accessory-variant], [data-accessory-quantity-change]';
        if (!event.target.closest(selector)) return;
        this.refreshPrices();
      };

      writePrice(node, price, showCurrencyCode = true, maxPrice = price) {
        if (!node) return;
        const formatter = new Intl.NumberFormat(this.dataset.locale, {
          style: 'currency',
          currency: this.dataset.currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        node.textContent = formatter.format(price / 100) + (maxPrice > price ? ` – ${formatter.format(maxPrice / 100)}` : '');
        if (!showCurrencyCode) return;
        node.append(' ');
        const currencyCode = document.createElement('span');
        currencyCode.className = 'price-item__currency-code';
        currencyCode.textContent = this.dataset.currency;
        node.append(currencyCode);
      }

      replaceOffer(source) {
        const refreshedAddons = new Map(Array.from(source.closest('product-info')?.querySelectorAll('[data-addon-pricing]') || [])
          .map((node) => [node.dataset.variantId || node.getAttribute('value'), node]));
        this.closest('product-info')?.querySelectorAll('[data-addon-pricing]').forEach((node) => {
          const refreshed = refreshedAddons.get(node.dataset.variantId || node.getAttribute('value'));
          if (!refreshed) return;
          for (const key of ['price', 'originalPrice', 'compareAtPrice', 'discountedPrice', 'startsAt', 'endsAt', 'offerCode']) {
            if (refreshed.dataset[key] === undefined) delete node.dataset[key];
            else node.dataset[key] = refreshed.dataset[key];
          }
        });
        for (const [key, value] of Object.entries(source.dataset)) this.dataset[key] = value;
        this.innerHTML = source.innerHTML;
        this.refreshPrices();
      }

      async fetchOffer() {
        if (!this.dataset.currentVariantId) return;
        this.abortController?.abort();
        this.abortController = new AbortController();
        const variantId = this.dataset.currentVariantId;
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('variant', variantId);
        try {
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Offer request failed: ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = html.getElementById(`DiscountOffer-${this.dataset.sectionId}`)
            ?.querySelector('product-discount-offer');
          if (source && source.dataset.currentVariantId === variantId && this.isConnected && this.dataset.currentVariantId === variantId) {
            this.replaceOffer(source);
          }
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }

      handleVisibilityChange = () => {
        if (document.hidden) return;
        this.refreshPrices();
        if (!this.pricing || !(this.pricing.cartExpires > Date.now())) this.fetchOffer();
      };

      handleClick = async (event) => {
        const termsButton = event.target.closest('[data-discount-terms]');
        this.querySelectorAll('.product-discount-offer__terms-wrap.is-open').forEach((wrapper) => {
          if (!termsButton || !wrapper.contains(termsButton)) {
            wrapper.classList.remove('is-open');
            wrapper.querySelector('[data-discount-terms]')?.setAttribute('aria-expanded', 'false');
          }
        });

        if (termsButton && this.contains(termsButton)) {
          const wrapper = termsButton.closest('.product-discount-offer__terms-wrap');
          const isOpen = wrapper.classList.toggle('is-open');
          termsButton.setAttribute('aria-expanded', String(isOpen));
          return;
        }

        const button = event.target.closest('[data-discount-copy]');
        if (!button || !this.contains(button)) return;

        try {
          await this.copyCode(button.dataset.code);
          this.showCopiedButtonState(button);
          this.showCopyToast(button.dataset.copiedLabel);
        } catch (error) {
          console.error('Unable to copy discount code.', error);
        }
      };

      handleKeydown = (event) => {
        if (event.key !== 'Escape') return;

        this.querySelectorAll('.product-discount-offer__terms-wrap.is-open').forEach((wrapper) => {
          wrapper.classList.remove('is-open');
          wrapper.querySelector('[data-discount-terms]')?.setAttribute('aria-expanded', 'false');
        });
      };

      async copyCode(code) {
        if (navigator.clipboard?.writeText) {
          try {
            await navigator.clipboard.writeText(code);
            return;
          } catch (error) {
            // Theme previews and some browsers block the Clipboard API; use the DOM fallback below.
          }
        }

        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        textarea.remove();
        if (!copied) throw new Error('Copy command failed.');
      }

      showCopiedButtonState(button) {
        const label = button.querySelector('[data-discount-cta-label]');
        if (!label) return;

        window.clearTimeout(this.copyButtonTimeout);
        const originalLabel = label.textContent;
        label.textContent = button.dataset.copiedButtonLabel;
        this.copyButtonTimeout = window.setTimeout(() => {
          label.textContent = originalLabel;
        }, 2400);
      }

      showCopyToast(message) {
        let toast = document.querySelector('[data-global-discount-copy-toast]');
        if (!toast) {
          toast = document.createElement('div');
          toast.className = 'product-discount-offer__toast';
          toast.dataset.globalDiscountCopyToast = '';
          toast.setAttribute('role', 'status');
          toast.setAttribute('aria-live', 'polite');
          document.body.appendChild(toast);
        }

        window.clearTimeout(toast.showTimeout);
        window.clearTimeout(toast.hideTimeout);
        toast.textContent = message;
        toast.hidden = false;
        requestAnimationFrame(() => toast.classList.add('is-visible'));

        toast.showTimeout = window.setTimeout(() => {
          toast.classList.remove('is-visible');
          toast.hideTimeout = window.setTimeout(() => {
            toast.hidden = true;
          }, 150);
        }, 2400);
      }
    }
  );
}
